/**
 * Dashboard pop-ups — Alert Actions' "Dashboard" channel: an alert Routing sent to THIS person,
 * shown on whatever page they are on (bottom-right, newest on top). Each app feeds it from its
 * own socket: the site app from its socket.io user room, the cloud app from /ws/user-events.
 *
 * A critical alert stays until it is opened or closed; others fade after AUTO_HIDE_MS. At most
 * MAX_SHOWN at once — older ones are dropped, the alert itself is still on the board and lists.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { BellAlertIcon, XMarkIcon } from '@heroicons/react/24/outline';

export interface AlertPopup {
  /** The alert's id (AI) or the system incident's. */
  id: string | null;
  kind: 'ai' | 'system';
  /** Cloud: raised by the cloud itself — a site offline, or the cloud server (opens the System Alerts page). */
  cloudRaised?: boolean;
  title: string;
  severity?: string | null;
  source?: string | null;
  message?: string | null;
  site_name?: string | null;
  /** Cloud: which site it came from. */
  node_id?: string | null;
  node_name?: string | null;
  at?: string | number | null;
  escalation?: { level?: number; levels?: number; reminder?: number; policy?: string } | null;
}

interface Shown extends AlertPopup { key: number }

const MAX_SHOWN = 4;
const AUTO_HIDE_MS = 20000;
const SEV_DOT: Record<string, string> = { critical: 'bg-destructive', high: 'bg-destructive', warning: 'bg-amber-500', medium: 'bg-amber-500' };

/** The pop-ups' state: push one in from a socket, dismiss one. */
export function useAlertPopups() {
  const [popups, setPopups] = useState<Shown[]>([]);
  const seq = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const dismiss = useCallback((key: number) => {
    setPopups((list) => list.filter((p) => p.key !== key));
    const t = timers.current.get(key);
    if (t) { clearTimeout(t); timers.current.delete(key); }
  }, []);
  const push = useCallback((p: AlertPopup) => {
    if (!p || !p.title) return;
    seq.current += 1;
    const key = seq.current;
    setPopups((list) => [{ ...p, key }, ...list.filter((x) => !(p.id && x.id === p.id && x.kind === p.kind))].slice(0, MAX_SHOWN));
    if (String(p.severity || '').toLowerCase() !== 'critical') {
      timers.current.set(key, setTimeout(() => dismiss(key), AUTO_HIDE_MS));
    }
  }, [dismiss]);
  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);
  return { popups, push, dismiss };
}

export interface AlertPopupsProps {
  popups: Shown[];
  dismiss: (key: number) => void;
  /** Open the alert (or the System alerts list) — the app knows its routes. */
  onOpen?: (p: AlertPopup) => void;
}

const timeText = (at: AlertPopup['at']) => {
  const d = at ? new Date(typeof at === 'number' && at < 1e12 ? at * 1000 : at) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
};

export default function AlertPopups({ popups, dismiss, onOpen }: AlertPopupsProps) {
  if (!popups.length) return null;
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[70] flex w-[22rem] max-w-[calc(100vw-2rem)] flex-col gap-2" role="region" aria-label="Alert pop-ups">
      {popups.map((p) => {
        const sev = String(p.severity || '').toLowerCase();
        const where = [p.source, p.node_name || p.site_name].filter(Boolean).join(' · ');
        const esc = p.escalation;
        return (
          <div key={p.key} role="alert" className={`pointer-events-auto rounded-xl border bg-card px-3 py-2.5 shadow-modal ${sev === 'critical' ? 'border-destructive' : 'border-border'}`}>
            <div className="flex items-start gap-2">
              <BellAlertIcon className={`mt-0.5 h-5 w-5 shrink-0 ${sev === 'critical' ? 'text-destructive' : 'text-muted-foreground'}`} />
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${SEV_DOT[sev] || 'bg-muted-foreground'}`} />
                  <span className="min-w-0 truncate" title={p.title}>{p.title}</span>
                </p>
                {where && <p className="truncate text-[12px] text-muted-foreground">{where}</p>}
                {esc && (
                  <p className="text-[12px] font-medium text-foreground">
                    {esc.reminder ? `Reminder ${esc.reminder} — still not acknowledged` : `Escalated to you — step ${esc.level || 2} of ${esc.levels || esc.level || 2}`}
                  </p>
                )}
                {p.message && <p className="mt-0.5 line-clamp-2 text-[12px] text-foreground">{p.message}</p>}
                <div className="mt-1.5 flex items-center gap-2">
                  {onOpen && (
                    <button type="button" onClick={() => { onOpen(p); dismiss(p.key); }} className="rounded-lg bg-brand px-2.5 py-1 text-[12px] font-medium text-white hover:opacity-90">
                      Open
                    </button>
                  )}
                  <span className="text-[11px] text-muted-foreground">{timeText(p.at)}</span>
                </div>
              </div>
              <button type="button" onClick={() => dismiss(p.key)} className="rounded-md p-0.5 text-muted-foreground hover:bg-muted" aria-label="Close">
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
